import { env } from '@/env'
import type { SharedWidget, WidgetInstance } from '@/types/generated/public_api'

/**
 * A share is ownerless, immutable and publicly readable forever, so anything
 * that survives this module is public the moment the link is copied. The
 * backend runs its own key denylist, but it only sees flat config keys — the
 * account ids that actually appear in a layout are buried inside JSON config
 * *values* (data-manager's per-color watchlists), which only this side can
 * reach. Treat the server's denylist as the safety net it is, not as cover for
 * skipping anything here.
 */

// Keys dropped both at the config-map boundary and at any depth inside a JSON
// value. `activeWatchlistId` is not secret, but it names a watchlist in the
// sharer's account that the recipient cannot resolve. `orderTypeGuide` is keyed
// by the sharer's user id (as a JSON key, which the markers below can't catch).
const PRIVATE_CONFIG_KEYS = [
  'account_id',
  'accountid',
  'activewatchlistid',
  'ordertypeguide',
  'user_id',
  'userid',
  'api_key',
  'apikey',
  'secret',
  'token',
  'password',
  'credential',
]

/**
 * Matches the backend's per-value cap, in **bytes** — DynamoDB's item limit is
 * bytes and the app ships ko/zh-CN locales, so counting UTF-16 units would let
 * a CJK-heavy config through at up to 1.5× its real size and fail server-side.
 * A chart with a lot of drawing state can exceed this; dropping that one entry
 * loses the drawings but still shares the layout, which beats failing outright.
 */
const MAX_CONFIG_VALUE_BYTES = 64 * 1024

/**
 * Matches the backend's total budget. Enforced here too because the server
 * answers a breach with a generic validation error, which the dialog can only
 * render as "try again" — and retrying never works. Dropping the largest
 * values instead makes the layout shareable and reports what was lost.
 */
const MAX_TOTAL_CONFIG_BYTES = 300 * 1024

export const DATA_MANAGER_WIDGET_TYPE = 'data-manager'

const encoder = new TextEncoder()
const byteLength = (value: string) => encoder.encode(value).length
const SHARED_WIDGET_INDEX_PREFIX = '__orderx_shared_widget_index__:'

/**
 * Substring, not equality — matching the server's own `is_sensitive_key`. An
 * exact-match list catches `accountId` and misses `subAccountId`,
 * `deribitAccountId` and `accountIds`, and for keys nested inside a JSON config
 * value this is the only layer there is.
 */
const isPrivateKey = (key: string, list: string[]) => {
  const lowered = key.toLowerCase()
  return list.some((marker) => lowered.includes(marker))
}

/** Recursively drop private keys. Returns `null` when nothing changed. */
function scrubJsonValue(value: unknown): { value: unknown; changed: boolean } {
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((entry) => {
      const result = scrubJsonValue(entry)
      changed ||= result.changed
      return result.value
    })
    return { value: changed ? next : value, changed }
  }

  if (value !== null && typeof value === 'object') {
    let changed = false
    const next: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      if (isPrivateKey(key, PRIVATE_CONFIG_KEYS)) {
        changed = true
        continue
      }
      const result = scrubJsonValue(entry)
      changed ||= result.changed
      next[key] = result.value
    }
    return { value: changed ? next : value, changed }
  }

  return { value, changed: false }
}

function mapWidgetReferences(
  value: unknown,
  resolve: (sourceCardId: string) => string
): { value: unknown; changed: boolean } {
  if (Array.isArray(value)) {
    let changed = false
    const next = value.map((entry) => {
      const result = mapWidgetReferences(entry, resolve)
      changed ||= result.changed
      return result.value
    })
    return { value: changed ? next : value, changed }
  }

  if (value !== null && typeof value === 'object') {
    let changed = false
    const next: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      if (key === 'sourceCardId' && typeof entry === 'string') {
        const resolved = resolve(entry)
        changed ||= resolved !== entry
        next[key] = resolved
        continue
      }
      const result = mapWidgetReferences(entry, resolve)
      changed ||= result.changed
      next[key] = result.value
    }
    return { value: changed ? next : value, changed }
  }

  return { value, changed: false }
}

function rewriteConfigWidgetReferences(
  config: Record<string, string>,
  resolve: (sourceCardId: string) => string
): Record<string, string> {
  let changed = false
  const next: Record<string, string> = {}

  for (const [key, raw] of Object.entries(config)) {
    try {
      const result = mapWidgetReferences(JSON.parse(raw), resolve)
      next[key] = result.changed ? JSON.stringify(result.value) : raw
      changed ||= result.changed
    } catch {
      next[key] = raw
    }
  }

  return changed ? next : config
}

/** Resolve portable intra-layout references after preview/import ids are allocated. */
export function resolveSharedWidgetReferences(
  config: Record<string, string>,
  widgetIds: readonly string[]
): Record<string, string> {
  return rewriteConfigWidgetReferences(config, (sourceCardId) => {
    if (!sourceCardId.startsWith(SHARED_WIDGET_INDEX_PREFIX)) return sourceCardId
    const index = Number(sourceCardId.slice(SHARED_WIDGET_INDEX_PREFIX.length))
    return Number.isInteger(index) && widgetIds[index] ? widgetIds[index] : sourceCardId
  })
}

export interface StripConfigResult {
  config: Record<string, string>
  /** Entries dropped for exceeding the per-value cap, not for being private. */
  droppedOversized: number
  /** Bytes the kept entries occupy, for the whole-layout budget check. */
  bytes: number
}

export function stripPrivateConfig(config: Record<string, string>): StripConfigResult {
  const result: Record<string, string> = {}
  let droppedOversized = 0
  let bytes = 0

  for (const [key, raw] of Object.entries(config ?? {})) {
    if (isPrivateKey(key, PRIVATE_CONFIG_KEYS)) continue

    let value = raw
    // Config values are opaque strings, so do not guess whether one is JSON
    // from its first character. Valid JSON may begin with whitespace; skipping
    // it would let a nested credential bypass the only recursive scrubber.
    try {
      const scrubbed = scrubJsonValue(JSON.parse(value))
      if (scrubbed.changed) value = JSON.stringify(scrubbed.value)
    } catch {
      // Not JSON — leave the scalar alone.
    }

    const size = byteLength(value)
    if (size > MAX_CONFIG_VALUE_BYTES) {
      droppedOversized += 1
      continue
    }
    result[key] = value
    bytes += byteLength(key) + size
  }

  return { config: result, droppedOversized, bytes }
}

export interface SharedLayoutBody {
  widgets: SharedWidget[]
  droppedOversized: number
}

/**
 * Turn a live page's widgets into the frozen share body. `widgetId`, `pageId`
 * and `updatedAt` are deliberately not carried: they identify a widget inside
 * one user's page and mean nothing in a copy.
 */
export function toSharedWidgets(widgets: readonly WidgetInstance[]): SharedLayoutBody {
  let droppedOversized = 0
  const widgetIndexById = new Map(widgets.map((widget, index) => [widget.widgetId, index]))
  const shared = widgets.map((widget) => {
    const portableConfig = rewriteConfigWidgetReferences(widget.config ?? {}, (sourceCardId) => {
      const index = widgetIndexById.get(sourceCardId)
      return index === undefined ? sourceCardId : `${SHARED_WIDGET_INDEX_PREFIX}${index}`
    })
    const stripped = stripPrivateConfig(portableConfig)
    droppedOversized += stripped.droppedOversized
    return {
      widget: {
        widgetType: widget.widgetType,
        x: widget.x,
        y: widget.y,
        w: widget.w,
        h: widget.h,
        minW: widget.minW,
        minH: widget.minH,
        config: stripped.config,
      },
      bytes: stripped.bytes,
    }
  })

  // The whole-layout budget. Shed the biggest config entries — the chart
  // drawing state that is doing the damage — until it fits, rather than
  // handing the server a body it can only refuse.
  droppedOversized += enforceTotalBudget(shared)

  return { widgets: shared.map((entry) => entry.widget), droppedOversized }
}

interface SizedWidget {
  widget: SharedWidget
  bytes: number
}

/** Drop largest-first until the layout fits. Returns how many entries went. */
function enforceTotalBudget(entries: SizedWidget[]): number {
  let total = entries.reduce((sum, entry) => sum + entry.bytes, 0)
  if (total <= MAX_TOTAL_CONFIG_BYTES) return 0

  const candidates = entries
    .flatMap((entry) =>
      Object.entries(entry.widget.config).map(([key, value]) => ({
        entry,
        key,
        bytes: byteLength(key) + byteLength(value),
      }))
    )
    .sort((a, b) => b.bytes - a.bytes)

  let dropped = 0
  for (const candidate of candidates) {
    if (total <= MAX_TOTAL_CONFIG_BYTES) break
    delete candidate.entry.widget.config[candidate.key]
    candidate.entry.bytes -= candidate.bytes
    total -= candidate.bytes
    dropped += 1
  }
  return dropped
}

/**
 * `orderx.trade` is the short form worth pasting into a chat. Verified
 * 2026-09-09: `orderx.trade/sl/<id>` 301s to `app.orderx.com/sl/<id>`, i.e. the
 * path survives the redirect. It does not resolve to staging or a local dev
 * server, so everywhere else builds the link off the origin the user is on.
 */
export function getSharedLayoutUrl(shareId: string): string {
  const path = `/sl/${shareId}`
  if (env.NEXT_PUBLIC_ENV === 'prod') return `https://orderx.trade${path}`
  if (typeof window !== 'undefined') return `${window.location.origin}${path}`
  return `${env.NEXT_PUBLIC_BASE_URL}${path}`
}

const PREVIEW_PAGE_ID_PREFIX = 'share-'

/**
 * The page id a share is previewed under. It is not one of the viewer's pages
 * and never reaches the server — it exists so per-page client state (linking
 * groups, widget config atoms) is scoped to the preview instead of leaking into
 * whichever page the viewer actually has open.
 */
export function getSharePreviewPageId(shareId: string): string {
  return `${PREVIEW_PAGE_ID_PREFIX}${shareId}`
}

export function isSharePreviewPageId(pageId: string): boolean {
  return pageId.startsWith(PREVIEW_PAGE_ID_PREFIX)
}

const SHARE_PREVIEW_PATH_PREFIX = '/trade/shared/'

/** In-app route the public `/sl/<shareId>` link forwards to. */
export function getSharePreviewPath(shareId: string): string {
  return `${SHARE_PREVIEW_PATH_PREFIX}${shareId}`
}

/**
 * A share preview lives under `/trade/` but is not one of the viewer's pages,
 * so anything that reads the route to decide which page is open — the page tabs
 * above all — has to exclude it.
 */
export function isSharePreviewPath(pathname: string): boolean {
  return pathname.startsWith(SHARE_PREVIEW_PATH_PREFIX)
}
